import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import Spinner from "../../common/Spinner";
import ConfirmDialog from "../../common/ConfirmDialog";
import {
    updateorganizationStatus,
    getOrganization,
} from "../../../redux/actions/OrganizationAction";

const DetailRow = ({ label, value }) => (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ py: 0.75 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 160, fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography variant="body2">{value || "—"}</Typography>
    </Stack>
);

const OrganizationInfo = () => {
    const dispatch = useDispatch();
    const organization = useSelector((s) => s?.organizationEdit?.organization);
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(null); // { nextActive: boolean }

    const applyToggle = () => {
        if (!confirm) return;
        const nextActive = confirm.nextActive;
        setConfirm(null);
        setLoading(true);
        dispatch(updateorganizationStatus({ id: organization?.id, isActive: nextActive })).then(() => {
            setLoading(false);
            dispatch(getOrganization(organization?.id));
        });
    };

    const isActive = organization?.isActive === 1 || organization?.isActive === true;

    return (
        <>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider", mt: 1 }}>
                <Typography variant="h4" component="h2" sx={{ mb: 2 }}>
                    About this organization
                </Typography>

                <Box>
                    <DetailRow label="Name" value={organization?.name} />
                    <DetailRow label="Email" value={organization?.email} />
                    <DetailRow label="Phone" value={organization?.phone} />
                    <DetailRow label="Address" value={organization?.address} />
                    <DetailRow label="Created by" value={organization?.createdBy} />
                </Box>

                <Divider sx={{ my: 3 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={isActive}
                            onChange={() => setConfirm({ nextActive: !isActive })}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {isActive ? "Active" : "Inactive"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {isActive
                                    ? "Members can sign in, upload builds, and install from this organization."
                                    : "Members cannot sign in until this organization is reactivated."}
                            </Typography>
                        </Box>
                    }
                />
            </Paper>

            <ConfirmDialog
                open={Boolean(confirm)}
                title={confirm?.nextActive ? `Activate ${organization?.name}?` : `Deactivate ${organization?.name}?`}
                body={
                    confirm?.nextActive
                        ? `Members of ${organization?.name} will regain access immediately.`
                        : `Members of ${organization?.name} will lose access until reactivated.`
                }
                confirmLabel={confirm?.nextActive ? "Activate" : "Deactivate"}
                confirmColor={confirm?.nextActive ? "primary" : "error"}
                onCancel={() => setConfirm(null)}
                onConfirm={applyToggle}
            />

            {loading && <Spinner fullscreen label="Saving…" />}
        </>
    );
};

export default OrganizationInfo;
